<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import SearchHighlightText from '@/components/createdUi/SearchHighlightText.vue'
import AnimatedLogoLoader from '@/components/createdUi/AnimatedLogoLoader.vue'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import type { ITreeSelectStore } from '@/types/ITreeSelectStore'
import type { IFilterChoiceItem } from '@/modules/universalStats/types/IFilterChoiceItem'
import type { IUniversalTreeSelectModule } from '@/types/IUniversalTreeSelectModule'

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number | null): void
  (e: 'change', item: IFilterChoiceItem): void
  (e: 'close'): void
}>()

const feedbackStore = useFeedbackStore()

/**
 * UniversalTreeSelect - компонент выбора из дерева для форм.
 * - Изолированный стор (treeChoices, treeLoading, treeCursors)
 * - Безопасная подсветка поиска через SearchHighlightText
 * - Серверный поиск и Infinite Scroll
 */

interface Props {
  fieldKey: string
  module?: IUniversalTreeSelectModule
  store: ITreeSelectStore
  modelValue: string | number | null
  placeholder?: string
  onlyLeaves?: boolean // Новый пропс для ограничения выбора только последних элементов в деревер
  /** При true — всегда перезапрашивает дерево (открытие меню). Без пропа — кэш как раньше */
  active?: boolean
}

// --- Внутреннее состояние ---
const search = ref('')
const open = ref<any[]>([])
const activated = ref<any[]>([])
const observerElement = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null

// --- Вычисления ---
const fieldKey = props.fieldKey as string

const displayItems = computed<IFilterChoiceItem[]>(() => {
  const rawItems = search.value
    ? (props.store.treeSearchChoices[props.fieldKey] || [])
    : (props.store.treeChoices[props.fieldKey] || [])

  if (!rawItems.length) return []

  return rawItems
})

watch(() => props.modelValue, newVal => {
  activated.value = newVal !== null && newVal !== undefined && newVal !== '' ? [newVal] : []
}, { immediate: true })

const onNodeSelect = (item: IFilterChoiceItem) => {
  // Проверяем, является ли узел "листом" (нет дочерних элементов)
  const isLeaf = !item.children || item.children.length === 0

  // Если включен режим "только листья" и это не лист — ничего не делаем
  if (props.onlyLeaves && !isLeaf) return

  activated.value = [item.id]
  emit('update:modelValue', item.id)

  emit('change', {
    id: item.id,
    label: item.label,
  })

  emit('close')
}

const handleInfiniteScroll = async () => {
  const cursor = props.store.treeCursors[props.fieldKey]
  const isLoading = props.store.treeLoading[props.fieldKey]

  if (cursor && !isLoading) {
    let cursorValue: string | null
    try {
      cursorValue = new URL(cursor).searchParams.get('cursor') || cursor
    } catch {
      cursorValue = cursor
    }

    await props.store.fetchTreeChoices(
      props.fieldKey,
      search.value,
      cursorValue,
      props.module,
    )
  }
}

// --- Логика наблюдения ---

watchDebounced(search, async val => {
  await props.store.fetchTreeChoices(
    props.fieldKey,
    val || '',
    undefined,
    props.module,
  )
  if (val && displayItems.value.length > 0) {
    open.value = displayItems.value.map(i => i.id)
  }
}, { debounce: 400 })

watch(displayItems, items => {
  if (!items?.length) return

  const idsWithChildren: (string | number)[] = []

  const collect = (nodes: IFilterChoiceItem[]) => {
    for (const node of nodes) {
      if (node.children && node.children.length > 0) {
        idsWithChildren.push(node.id)
        collect(node.children)
      }
    }
  }

  collect(items)
  open.value = idsWithChildren
}, { immediate: true })

const loadChoices = async (searchVal?: string) => {
  await props.store.fetchTreeChoices(
    props.fieldKey,
    searchVal,
    undefined,
    props.module,
  )
}

/* Открытие меню — свежий запрос (в т.ч. соседняя вкладка после PATCH в админке) */
watch(() => props.active, async isActive => {
  if (!isActive) return
  await loadChoices(search.value || undefined)
})

onMounted(async () => {
  if (props.active === undefined) {
    feedbackStore.isGlobalLoading = true
    try {
      if (!props.store.treeChoices[props.fieldKey]?.length)
        await loadChoices()
    } finally {
      feedbackStore.isGlobalLoading = false
    }
  } else if (props.active) {
    await loadChoices()
  }

  observer = new IntersectionObserver(async ([entry]) => {
    if (entry.isIntersecting) await handleInfiniteScroll()
  }, { threshold: 0.1 })

  if (observerElement.value) observer.observe(observerElement.value)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<template>
  <VCard class="universal-tree-select-container d-flex flex-column" elevation="8">
    <VSheet class="pa-2 flex-grow-0">
      <VTextField
        v-model="search"
        :placeholder="placeholder || 'Поиск...'"
        variant="outlined"
        rounded="pill"
        class="search-field"
        :append-inner-icon="search ? undefined : 'ri-search-line'"
        hide-details
        clearable
        autofocus
        autocomplete="off"
      />
    </VSheet>

    <VDivider />

    <VSheet class="tree-view-content flex-grow-1">
      <VTreeview
        v-model:activated="activated"
        v-model:opened="open"
        :items="displayItems"
        item-value="id"
        item-title="label"
        item-children="children"
        activatable
        transition
        color="primary"
        class="custom-treeview"
      >
        <template #title="{ item }">
          <div
            class="text-body-1 text-high-emphasis py-1 text-wrap cursor-pointer"
            :class="(props.onlyLeaves && item.children?.length) ? 'cursor-default opacity-70' : 'cursor-pointer'"
            @click.stop="onNodeSelect(item)"
          >
            <SearchHighlightText
              :text="item.label"
              :query="search"
            />
          </div>
        </template>

        <template #toggle="{ props: toggleProps, item }">
          <VIconBtn
            v-if="item.children && item.children.length > 0"
            v-bind="toggleProps"
            icon="ri-play-fill"
            class="expand-btn"
            variant="text"
            :class="{ expanded: open.includes(item.id as any) }"
          />
          <div v-else class="node-leaf-spacer" />
        </template>
      </VTreeview>

      <div ref="observerElement" class="d-flex justify-center align-center py-4" style="min-height: 60px;">
        <AnimatedLogoLoader
          v-if="store.treeLoading[fieldKey]"
        />
        <span v-else-if="!store.treeCursors[fieldKey] && displayItems.length > 0" class="text-caption text-disabled">
          Конец классификатора
        </span>
      </div>
    </VSheet>
  </VCard>
</template>

<style scoped lang="scss">
.universal-tree-select-container {
  .text-wrap {
    white-space: normal;
    word-break: break-word;
    line-height: 1.4;
  }
}

.tree-view-content {
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 0.5rem;
    background: transparent;
  }

  &::-webkit-scrollbar-track {
    background: rgb(var(--v-theme-secondary), var(--v-activated-opacity));
    border-radius: 0.5rem;
  }

  &::-webkit-scrollbar-thumb {
    background: rgb(var(--v-theme-secondary), 0.32) !important;
    border-radius: 0.5rem;
    inline-size: 0.25rem;
    min-height: 6.25rem !important;

    &:hover {
      inline-size: 0.375rem;
      background: rgb(var(--v-theme-primary)) !important;
    }
  }
}
</style>
